import React, { useState } from 'react';
import { Card, CardContent } from '../../../../../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../../../components/ui/table';
import { FileText, Edit, Trash2, Upload, Wrench, Split } from 'lucide-react';
import { cn } from '../../../../../../lib/utils';
import { MaterialData } from '../../../../types/materials';
import { ForgeFilters } from './Filters/ForgeFilters';
import { useForgeFilters } from './Filters/hooks/useForgeFilters';
import { Button } from '../../../../../../components/ui/button';
import { DeleteConfirmationDialog, DeleteItem } from '../../../../../../components/ui/DeleteConfirmationDialog';
import { Pagination } from '../../../../../../components/ui/Pagination';
import { SortableTableHead } from '../../../../../../components/ui/SortableTableHead';
import { useTableSort } from '../../../../../../hooks/useTableSort';
import { deleteMaterials, DeleteMaterialDocument } from '../../../../../../redux/Project/materialSaga';
import { UpdateMaterialDialog } from '../UpdateMaterialDialog';
import { DocumentDownloadModal } from '../../specifications/DocumentDownloadModal';
import { MaterialDocumentUploadModal } from '../MaterialDocumentUploadModal';
import { DocumentTag } from '../../../../../../features/project/types';
import { formatDate } from '../../../../../../utils/unitUtils';
import { PipeSegmentationModal } from './PipeSegmentationModal';

function getMaterialStatusDisplay(material: MaterialData): string {
  const extra = material.materialExtraDetails as { materialStatus?: string } | undefined;
  const nested = (material.details as any)?.material_extra_details;
  return (
    extra?.materialStatus ??
    nested?.materialStatus ??
    material.materialStatus ??
    '-'
  );
}

interface ForgeTabProps {
  materials: MaterialData[];
  selectedPipeSize: number;
  currentPage: number;
  materialsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  refreshMaterials: () => Promise<void>;
  onRowDelete?: (materialId: string | number, category: string) => Promise<void>;
  deletingRows?: Set<string>;
  documentTags: DocumentTag[];
  setDocumentTags: (any: any) => void;
  pipeSizesData: any;
  canEdit?: boolean;
  onOpenViewer?: (fileOrFiles: any | any[]) => void;
}

export const ForgeTab: React.FC<ForgeTabProps> = React.memo(({
  materials,
  selectedPipeSize,
  currentPage,
  materialsPerPage,
  onPageChange,
  onItemsPerPageChange,
  refreshMaterials,
  onRowDelete,
  deletingRows,
  documentTags,
  setDocumentTags,
  pipeSizesData,
  canEdit = true,
  onOpenViewer
}) => {
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [itemToUpdate, setItemToUpdate] = useState<MaterialData | null>(null);
  const [isDocumentModalVisible, setIsDocumentModalVisible] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<MaterialData | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isSegmentationModalOpen, setIsSegmentationModalOpen] = useState(false);
  const [selectedForgeForSegmentation, setSelectedForgeForSegmentation] = useState<MaterialData | null>(null);
  const [isDocumentDeleteModalOpen, setIsDocumentDeleteModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<any>(null);
  const [deleteDocumentLoading, setDeleteDocumentLoading] = useState(false);

  // Use the forge filters hook
  const {
    searchQuery,
    setSearchQuery,
    filters,
    onFilterChange,
    uniqueNRIs,
    uniqueOrderNumbers,
    uniqueSerialNumbers,
    uniqueHeatNumbers,
    uniqueForgeNRIs,
    uniqueForgeProjectStages,
    filteredMaterials,
    hasActiveFilters,
    filteredCount,
    onResetFilters,
  } = useForgeFilters({ materials });

  // Filter materials to only show Forge category
  const forgeMaterials = filteredMaterials.filter((material: MaterialData) => material.materialCategory === 'Forge');

  // Add sorting functionality
  const { sortedData: sortedForgeMaterials, handleSort, getSortDirection } = useTableSort(forgeMaterials);


  const handleConfirmDelete = async () => {
    try {
      setDeleteLoading(true);

      if (onRowDelete) {
        await onRowDelete(itemToDelete?.materialId, itemToDelete?.materialCategory);
        setIsDeleteModalVisible(false);
        setItemToDelete(null);
        setDeleteLoading(false);
      } else {
        const res = await deleteMaterials([itemToDelete?.materialId], itemToDelete?.materialCategory);
        if (res) {
          setIsDeleteModalVisible(false);
          setItemToDelete(null);
          setDeleteLoading(false);
          refreshMaterials();
        }
      }
    } catch (error) {
      setDeleteLoading(false);
      setIsDeleteModalVisible(false);
      return null;
    }
  }

  const handleUpdateMaterial = async (updatedMaterial: MaterialData) => {
    await refreshMaterials();
  };

  const handleUpload = (files: FileList | null) => {
    // Handle file upload
    refreshMaterials();
  };

  // Handle document operations
  const handleDocumentClick = (material: MaterialData) => {
    setSelectedMaterial(material);
    setIsDocumentModalVisible(true);
  };
  const handleDocumentDownload = (document: any) => {
    // Close the modal first
    setIsDocumentModalVisible(false);
    
    if (onOpenViewer) {
      onOpenViewer(document);
    } else if (document.fileUrl) {
      window.open(document.fileUrl, '_blank');
    }
  };

  const handleDeleteDocument = (doc: any) => {
    setDocumentToDelete(doc);
    setIsDocumentDeleteModalOpen(true);
  };

  const handleCancelDeleteDocument = () => {
    setIsDocumentDeleteModalOpen(false);
    setDocumentToDelete(null);
  };

  const handleConfirmDeleteDocument = async () => {
    if (!documentToDelete) return;

    setDeleteDocumentLoading(true);
    try {
      // Get materialTraceabilityId from the document
      const materialTraceabilityId = documentToDelete.id || documentToDelete.materialTraceabilityId;
      
      if (!materialTraceabilityId) {
        console.error('Material traceability ID not found', documentToDelete);
        setDeleteDocumentLoading(false);
        return;
      }

      const payload = {
        materialDocsIds: [Number(materialTraceabilityId)]
      };

      const deleteResponse = await DeleteMaterialDocument(payload);

      if (deleteResponse) {
        // Refresh materials after successful deletion
        refreshMaterials();
        // Close the delete modal
        handleCancelDeleteDocument();
        // Close the document modal if it's open
        setIsDocumentModalVisible(false);
        setSelectedMaterial(null);
      }
    } catch (error) {
      console.error('Error deleting document:', error);
    } finally {
      setDeleteDocumentLoading(false);
    }
  };

  // Handle segmentation operations
  const handleSegmentationClick = (forge: MaterialData) => {
    setSelectedForgeForSegmentation(forge);
    setIsSegmentationModalOpen(true);
  };

  const handleSaveSegments = async (segments: any[]) => {
    try {
      if (selectedForgeForSegmentation) {
        // Update the material with segmentation data
        const updatedMaterial = {
          ...selectedForgeForSegmentation,
          details: {
            ...selectedForgeForSegmentation.details,
            forgeSegmentation: segments
          }
        };
        
        // Call the update function (you may need to implement this)
        await refreshMaterials();
      }
    } catch (error) {
      console.error('Error saving segments:', error);
    }
  };

  // Helper function to transform documents for the modal
  const transformDocumentsForModal = (material: MaterialData) => {
    const documents = material.documents || [];
    return documents.flatMap(doc => {
      // Handle the new API structure with materials array
      if ((doc as any).materials && Array.isArray((doc as any).materials)) {
        return (doc as any).materials.map((mat: any) => ({
          id: mat.materialTraceabilityId || Math.random().toString(),
          fileName: mat.fileName,
          fileUrl: mat.fileUrl,
          uploadDate: mat.uploadedAt || mat.createdAt,
          type: 'document'
        }));
      }
      // Handle legacy structure
      return [{
        id: (doc as any).id || Math.random().toString(),
        fileName: (doc as any).fileName,
        fileUrl: (doc as any).fileUrl,
        uploadDate: (doc as any).uploadedAt,
        type: 'document'
      }];
    });
  };

  const renderItemDetails = (item: DeleteItem) => {
    return (
      <div className="text-sm text-gray-700 py-2 border-b border-gray-200 last:border-b-0">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
          <div>• NRI: {item.details?.forgeNRI}</div>
          <div>• Order Number: {item.details?.forgeWorkOrderNumber}</div>
          <div>• Serial Number: {item.details?.forgeSerialNumber}</div>
          <div>• Heat Number: {item.details?.forgeHeatNumber}</div>
        </div>
      </div>
    );
  };



  // Check if there are any specifications for this subtype
  const hasAnyMaterials = forgeMaterials.length > 0;

  // If no specifications exist for any pipes in this subtype, show empty state
  if (!hasActiveFilters && !hasAnyMaterials) {
    return (
      <div className="space-y-6">

        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <Wrench className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No forges created yet</h3>
          <p className="text-gray-500 max-w-sm">
            Start by adding forges for your forges project. Click the "Add Material" button to get started.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {/* Forge Filters */}
        <ForgeFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          filters={filters}
          onFilterChange={onFilterChange}
          uniqueNRIs={uniqueNRIs}
          uniqueOrderNumbers={uniqueOrderNumbers}
          uniqueSerialNumbers={uniqueSerialNumbers}
          uniqueHeatNumbers={uniqueHeatNumbers}
          uniqueForgeNRIs={uniqueForgeNRIs}
          uniqueForgeProjectStages={uniqueForgeProjectStages}
          hasActiveFilters={hasActiveFilters}
          filteredCount={forgeMaterials.length}
          onResetFilters={onResetFilters}
        />

        <Card>
          <CardContent className="p-4">
              <div className="overflow-x-auto">
                <div className="overflow-y-auto max-h-[400px] md:max-h-[500px] lg:max-h-[calc(100vh-350px)]">
                 <Table className="min-w-[1300px]" containerClassName="">
                    <TableHeader className="sticky top-0 z-20 bg-white">
                    <TableRow>
                  <SortableTableHead
                    sortKey="details.forgeNRI"
                    onSort={handleSort}
                    sortDirection={getSortDirection("details.forgeNRI")}
                  >
                    NRI
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="details.forgeWorkOrderNumber"
                    onSort={handleSort}
                    sortDirection={getSortDirection("details.forgeWorkOrderNumber")}
                  >
                    Order Number
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="details.forgeSerialNumber"
                    onSort={handleSort}
                    sortDirection={getSortDirection("details.forgeSerialNumber")}
                  >
                    Serial Number
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="details.forgeHeatNumber"
                    onSort={handleSort}
                    sortDirection={getSortDirection("details.forgeHeatNumber")}
                  >
                    Heat Number
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="details.forgeProjectStage"
                    onSort={handleSort}
                    sortDirection={getSortDirection("details.forgeProjectStage")}
                    className="text-center"
                  >
                    Project Stage
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="materialStatus"
                    onSort={handleSort}
                    sortDirection={getSortDirection("materialStatus")}
                  >

                    Status
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="assemblyNumbers"
                    onSort={handleSort}
                    sortDirection={getSortDirection("assemblyNumbers")}
                    className="text-center"
                  >
                    Assembly Number
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="materialDate"
                    onSort={handleSort}
                    sortDirection={getSortDirection("materialDate")}
                    className="text-center w-[100px]"
                  >
                    Date
                  </SortableTableHead>
                  <TableHead className="text-center">Documents</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedForgeMaterials.length > 0 ? sortedForgeMaterials
                  .slice((currentPage - 1) * materialsPerPage, currentPage * materialsPerPage)
                  .map((forge) => {
                    const statusDisplay = getMaterialStatusDisplay(forge);
                    return (
                    <TableRow key={forge.materialId}>
                      <TableCell>{forge.details?.forgeNRI}</TableCell>
                      <TableCell>{forge.details?.forgeWorkOrderNumber}</TableCell>
                      <TableCell>{forge.details?.forgeSerialNumber}</TableCell>
                      <TableCell>{forge.details?.forgeHeatNumber}</TableCell>
                      <TableCell className="text-center">{forge.details?.forgeProjectStage}</TableCell>
                      <TableCell>
                        <span
                          className={cn(
                            'px-2 py-1 text-xs rounded-full',
                            statusDisplay === 'Available' && 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]',
                            statusDisplay === 'Consumed' && 'bg-gray-100 text-gray-800',
                            statusDisplay === 'Shipped to NDT' && 'text-[#B38600] bg-[rgba(230,161,0,0.08)]'
                          )}
                        >
                          {statusDisplay}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">{forge.assemblyNumbers && forge.assemblyNumbers.length > 0 ? forge.assemblyNumbers?.join(', ') || '-' : '-'}</TableCell>
                      <TableCell className="text-center w-[150px]">{forge.materialDate ? formatDate(forge.materialDate) : '-'}</TableCell>
                      <TableCell className="text-center w-[80px]">
                        <div className={`flex items-center gap-2 ${!forge.documents || forge.documents.length === 0 ? 'justify-center' : ''}`}>
                          <Button
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-800 w-8 h-8 p-0 flex items-center justify-center"
                            onClick={() => {
                              setSelectedMaterial(forge);
                              setIsUploadModalOpen(true);
                            }}
                            title={`Upload ${forge.materialCategory}`}
                            aria-label={`Upload ${forge.materialCategory}`}
                            variant="outline"
                            disabled={!canEdit}
                          >
                            <Upload className="h-4 w-4" />
                          </Button>
                          {forge.documents && forge.documents.length > 0 &&
                            <Button
                              className="flex items-center gap-1 text-blue-600 hover:text-blue-800 w-8 h-8 p-0 flex items-center justify-center"
                              title="View Documents"
                              variant="outline"
                              onClick={() => handleDocumentClick(forge)}
                            >
                              <FileText className="h-4 w-4" />
                              {forge.documents.length}
                            </Button>
                          }
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center gap-2 justify-center">
                          <Button
                            onClick={() => {
                              setItemToUpdate(forge);
                              setIsUpdateModalVisible(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 w-8 h-8 p-0 flex items-center justify-center"
                            title="Edit Material"
                            variant="outline"
                            disabled={!canEdit || deletingRows?.has(String(forge.materialId))}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          {/* Segmentation button - only show if project stage is PQT */}
                          
                          <Button
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 w-8 h-8 p-0 flex items-center justify-center"
                            onClick={() => {
                              setIsDeleteModalVisible(true);
                              setItemToDelete(forge)
                            }}
                            title="Delete Material"
                            variant="outline"
                            disabled={!canEdit || deletingRows?.has(String(forge.materialId))}
                          >
                            {deletingRows?.has(String(forge.materialId)) ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>

                          {/* {forge.details?.forgeExtraDetails?.forgeProjectStage === 'PQT' && (
                            <Button
                              onClick={() => handleSegmentationClick(forge)}
                              className="text-green-600 hover:text-green-800 w-8 h-8 p-0 flex items-center justify-center"
                              title="Segmentation"
                              variant="outline"
                              disabled={deletingRows?.has(String(forge.materialId))}
                            >
                              <Split className="h-4 w-4" />
                            </Button>
                          )} */}
                        </div>
                      </TableCell>
                    </TableRow>
                  );}) : (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-gray-500">No forge materials found</TableCell>
                  </TableRow>
                )}
                </TableBody>
                </Table>
              </div>
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalItems={sortedForgeMaterials.length}
              itemsPerPage={materialsPerPage}
              onPageChange={onPageChange}
              onItemsPerPageChange={onItemsPerPageChange}
              pageSizeOptions={[10, 25, 50, 100]}
              showPageNumbers={true}
              maxVisiblePages={5}
            />
          </CardContent>
        </Card>
        <DeleteConfirmationDialog
          isOpen={isDeleteModalVisible}
          onClose={() => setIsDeleteModalVisible(false)}
          onConfirm={handleConfirmDelete}
          itemType="Material"
          itemName={itemToDelete?.details?.forgeNRI}
          itemToDelete={itemToDelete}
          detailsRenderer={renderItemDetails}
          isLoading={deleteLoading}
        />
        <UpdateMaterialDialog
          isOpen={isUpdateModalVisible}
          onClose={() => setIsUpdateModalVisible(false)}
          onUpdate={handleUpdateMaterial}
          material={itemToUpdate}
          project={{} as any}
          unit="inches"
          documentTags={documentTags}
          setDocumentTags={setDocumentTags}
          selectedPipe={pipeSizesData}
        />

        {/* Document Download Modal */}
        {selectedMaterial && (
          <DocumentDownloadModal
            isOpen={isDocumentModalVisible}
            onClose={() => {
              setIsDocumentModalVisible(false);
              setSelectedMaterial(null);
            }}
            documents={transformDocumentsForModal(selectedMaterial)}
            onDownload={handleDocumentDownload}
            onDelete={handleDeleteDocument}
            title="Forge Material Documents"
            canDelete={canEdit}
          />
        )}

        {/* Document Delete Confirmation Dialog */}
        <DeleteConfirmationDialog
          isOpen={isDocumentDeleteModalOpen}
          onClose={handleCancelDeleteDocument}
          onConfirm={handleConfirmDeleteDocument}
          isLoading={deleteDocumentLoading}
          itemToDelete={documentToDelete}
          itemType="Material Document"
          itemName={documentToDelete?.fileName || documentToDelete?.file_name || ''}
          title="Delete Material Document"
        />

        {/* Document Upload Modal */}
        <MaterialDocumentUploadModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          onUpload={handleUpload}
          title="Upload Material Document"
          acceptedTypes=".pdf,.doc,.docx,.xlsx,.xls"
          multiple={true}
          maxFiles={1}
          buttonText="Select Document"
          label="Document"
          description="Upload material documents (PDF, Excel, DOC, etc...)"
          material={selectedMaterial || undefined}
          documentTags={documentTags}
          setDocumentTags={setDocumentTags}
          materialType="Forge"
          selectedPipeSize={pipeSizesData}
        />

        {/* Forge Segmentation Modal */}
        {isSegmentationModalOpen && selectedForgeForSegmentation && (
          <PipeSegmentationModal
            isOpen={isSegmentationModalOpen}
            onClose={() => {
              setIsSegmentationModalOpen(false);
              setSelectedForgeForSegmentation(null);
            }}
            pipeId={selectedForgeForSegmentation.details?.forgeNRI || 'N/A'}
            heatNumber={selectedForgeForSegmentation.details?.forgeHeatNumber || 'N/A'}
            pipeNo={selectedForgeForSegmentation.details?.forgeSerialNumber || 'N/A'}
            onSave={handleSaveSegments}
            selectedMaterial={selectedForgeForSegmentation}
          />
        )}
      </div>
    </>
  );
}); 